import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { portfolio } from "../content";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const PortfolioCard = ({ item, index }) => (
  <motion.a
    href="#contact"
    data-testid={`portfolio-card-${item.id}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
      delay: (index % 3) * 0.08,
    }}
    className={`group relative overflow-hidden bg-taupe/10 ${item.span}`}
  >
    <motion.img
      src={item.image}
      alt={`${item.title} — ${item.label}`}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover will-change-transform"
      whileHover={{ scale: 1.03 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
    />

    {/* darken overlay on hover */}
    <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/25 transition-colors duration-500" />

    {/* Label — slides up + fades in on hover */}
    <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8 overflow-hidden">
      <div className="translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out">
        <p className="text-[11px] uppercase tracking-[0.3em] text-bone/80">
          {item.label} · {item.year}
        </p>
        <div className="mt-2 flex items-end justify-between gap-4">
          <h3 className="font-serif text-bone text-2xl md:text-3xl leading-tight">
            {item.title}
          </h3>
          <span className="inline-flex items-center gap-1 text-bone/90 text-xs">
            {item.location}
            <ArrowUpRight size={14} />
          </span>
        </div>
      </div>
    </div>
  </motion.a>
);

export const Portfolio = () => {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="bg-bone py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="flex items-end justify-between gap-8 mb-14 md:mb-20"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-10 h-px bg-taupe" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-taupe">
                Selected Work · 2022—2025
              </span>
            </div>
            <h2
              data-testid="portfolio-headline"
              className="font-serif text-charcoal text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em]"
            >
              The Grid.
            </h2>
            <p className="mt-5 text-charcoal/70 max-w-lg leading-relaxed">
              Six projects from our most recent chapters — each one a slow
              conversation between client, site, and material.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="portfolio-full-archive-link"
            className="hidden md:inline-flex items-center gap-2 text-sm text-charcoal border-b border-charcoal/40 hover:border-charcoal pb-1 transition-colors duration-300"
          >
            Request Full Archive
            <ArrowUpRight size={14} />
          </a>
        </motion.div>

        <div
          className="portfolio-grid"
          data-testid="portfolio-grid"
        >
          {portfolio.map((item, i) => (
            <PortfolioCard key={item.id} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
